/**
 * Hook: Withdraw flow — submit proofs + destination address, receive XMR.
 */

import { useState, useCallback, useRef } from 'react';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';

import { generateRequestId } from '@/lib/cashu/token';
import { createSeal, createGiftWrap, unwrapMessage } from '@/lib/nostr/gift-wrap';
import { getRequestKind, getResponseKind } from '@/lib/nostr/operations';
import type { WithdrawRequest, WithdrawResponse } from '@/lib/nostr/operations';
import { removeProofsBySecrets, addProofs, addTransaction } from '@/lib/storage/wallet-store';
import type { Proof } from '@/lib/cashu/proof';

export type WithdrawState =
  | { step: 'idle' }
  | { step: 'submitting' }
  | { step: 'awaiting_confirmation' }
  | { step: 'done'; txHash: string; amount: number }
  | { step: 'error'; error: string };

export function useWithdrawFlow() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const [state, setState] = useState<WithdrawState>({ step: 'idle' });
  const subRef = useRef<any>(null);

  const startWithdraw = useCallback(async (
    proofs: Proof[],
    destinationAddress: string,
    mintPubkey: string,
    userPrivkey: string,
  ) => {
    if (!user) {
      setState({ step: 'error', error: 'Not logged in' });
      return;
    }

    try {
      setState({ step: 'submitting' });

      const requestId = generateRequestId();
      const totalAmount = proofs.reduce((s, p) => s + p.amount, 0);

      const request: WithdrawRequest = {
        op: 'withdraw',
        requestId,
        proofs: proofs.map(p => ({ secret: p.secret, C: p.C, id: p.id, amount: p.amount })),
        destinationAddress,
      };

      const rumor = {
        kind: getRequestKind(),
        content: JSON.stringify(request),
        tags: [['p', mintPubkey]],
        created_at: Math.floor(Date.now() / 1000),
        pubkey: user.pubkey,
      };

      const seal = createSeal(rumor, userPrivkey, mintPubkey);
      const giftWrap = createGiftWrap(seal, mintPubkey);

      await nostr.event(giftWrap);
      setState({ step: 'awaiting_confirmation' });

      subRef.current = nostr.sub([{
        kinds: [1059],
        '#p': [user.pubkey],
        limit: 100,
      }]);

      subRef.current.on('event', (event: any) => {
        try {
          const { rumor: resp } = unwrapMessage(event, userPrivkey);
          if (resp.kind !== getResponseKind()) return;

          const response = JSON.parse(resp.content);
          if (response.requestId !== requestId) return;

          subRef.current?.unsub();

          if (response.status === 'error') {
            setState({ step: 'error', error: response.error });
            return;
          }

          const ok = response as WithdrawResponse;
          removeProofsBySecrets(proofs.map(p => p.secret));

          addTransaction({
            id: requestId,
            type: 'withdraw',
            mintNpub: mintPubkey,
            amount: totalAmount,
            timestamp: Date.now(),
            status: 'confirmed',
            txHash: ok.txHash,
          });

          if (ok.changeSignatures?.length) {
            // Change tokens would be handled here
          }

          setState({ step: 'done', txHash: ok.txHash, amount: totalAmount });
        } catch {
          // ignore
        }
      });
    } catch (err) {
      setState({ step: 'error', error: (err as Error).message });
    }
  }, [user, nostr]);

  const reset = useCallback(() => {
    subRef.current?.unsub();
    setState({ step: 'idle' });
  }, []);

  return { state, startWithdraw, reset };
}
